/**
 * App.js, v12 (portál)
 * Přihlášený režim (Supabase): záložky Dashboard ↔ Nová valuace + auto-uložení.
 * Bez Supabase: stará app za sdíleným heslem (PasswordGate).
 */
import React, { useEffect, useRef, useState } from 'react';
import { useWizardState } from './hooks/useWizardState';
import { Step0_CompanyInfo } from './components/Step0_CompanyInfo';
import { Step1_Finance } from './components/Step1_Finance';
import { StepSDE_ProfitCleaning } from './components/StepSDE_ProfitCleaning';
import { Step2_KPIs } from './components/Step2_KPIs';
import { Step3_TrafficBrand } from './components/Step3_TrafficBrand';
import { Step4_QualitativeData } from './components/Step4_QualitativeData';
import { Step5_Calculation } from './components/Step5_Calculation';
import { Step6_Results } from './components/Step6_Results';
import { Home } from './components/Home';
import { Dashboard } from './components/Dashboard';
import { AdminPanel } from './components/AdminPanel';
import { Crawler } from './components/Crawler';
import { Billing } from './components/Billing';
import { Consultation } from './components/Consultation';
import { Account } from './components/Account';
import { ValuationDetail } from './components/ValuationDetail';
import { PortalLayout } from './components/PortalLayout';
import { AssistantWidget } from './components/AssistantWidget';
import { ErrorBoundary } from './components/ErrorBoundary';
import { PasswordGate } from './components/PasswordGate';
import { AuthProvider, useAuth } from './auth/AuthProvider';
import { AuthScreen } from './auth/AuthScreen';
import { isSupabaseConfigured } from './lib/supabaseClient';
import { saveValuation, getValuation } from './lib/apiFetch';
import { buildValuationRequest } from './api/valuationCalculator';
import './styles/wizard.css';
import { klicKroku, popiskyProTyp, pocetKroku } from './utils/kroky';
import { Step_Zadani } from './components/Step_Zadani';

function ProgressBar({ currentStep, typPodnikani }) {
  // Popisky se řídí typem podnikání: u výroby ani služeb se na reklamu
  // a návštěvnost neptáme, takže ty kroky v liště nejsou.
  const STEP_LABELS = popiskyProTyp(typPodnikani);
  return (
    <div style={{ marginBottom: '2rem' }}>
      <div className="progress-bar">
        {STEP_LABELS.map((label, i) => {
          const step = i + 1;
          const isActive = currentStep === step;
          const isCompleted = currentStep > step;
          return (
            <React.Fragment key={step}>
              <div className={`progress-step ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}>
                {isCompleted ? '✓' : step}
              </div>
              {i < STEP_LABELS.length - 1 && (
                <div className={`step-connector ${currentStep > step ? 'active' : ''}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0 0.25rem', marginTop: '0.25rem' }}>
        {STEP_LABELS.map(label => (
          <div key={label} style={{ fontSize: '0.7rem', color: '#9ca3af', textAlign: 'center', flex: 1 }}>
            {label}
          </div>
        ))}
      </div>
    </div>
  );
}

function WizardView({ wizardState, setWizardState, nextStep, previousStep, reset, bypassPayment, goBack, hideAssistant }) {
  // „Zpět" jde přes historii prohlížeče (když je portál), jinak prostě o krok zpět
  const back = goBack || previousStep;
  // Při přechodu na další/předchozí krok srolovat nahoru (ne na konec předchozího)
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [wizardState.currentStep]);

  // Theme změny z parent okna (mnai web iframe)
  useEffect(() => {
    const handler = (e) => {
      if (e.data && e.data.type === 'theme-change') {
        document.documentElement.setAttribute('data-theme', e.data.theme);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  const renderStep = () => {
    // Krok se vybírá podle klíče, ne podle pořadového čísla. Pořadí se mění
    // s typem podnikání: u výroby odpadá krok o zákaznících i o návštěvnosti.
    const spolecne = { wizardState, setWizardState, onNext: nextStep, onBack: back };
    switch (klicKroku(wizardState.typ_podnikani, wizardState.currentStep)) {
      case 'zadani':     return <Step_Zadani wizardState={wizardState} setWizardState={setWizardState} onNext={nextStep} />;
      case 'spolecnost': return <Step0_CompanyInfo {...spolecne} />;
      case 'finance':    return <Step1_Finance {...spolecne} />;
      case 'zisk':       return <StepSDE_ProfitCleaning {...spolecne} />;
      case 'kpi':        return <Step2_KPIs {...spolecne} />;
      case 'traffic':    return <Step3_TrafficBrand {...spolecne} />;
      case 'kvalita':    return <Step4_QualitativeData {...spolecne} />;
      case 'vypocet':    return <Step5_Calculation {...spolecne} />;
      case 'vysledky':   return <Step6_Results wizardState={wizardState} setWizardState={setWizardState} onReset={reset} onBack={back} bypassPayment={bypassPayment} />;
      default: return null;
    }
  };

  return (
    <div className="wizard-container">
      <div className="wizard-header">
        <h1>Ocenění a audit podniku</h1>
        <p>Z jednoho vyplnění získáte hodnotu firmy i audit, kde ztrácíte zisk. Co odemknete, vyberete na konci.</p>
      </div>
      <ProgressBar currentStep={wizardState.currentStep} typPodnikani={wizardState.typ_podnikani} />
      <ErrorBoundary onReset={back}>
        {renderStep()}
      </ErrorBoundary>
      {!hideAssistant && (
        <AssistantWidget context={`Krok ${wizardState.currentStep}: ${popiskyProTyp(wizardState.typ_podnikani)[wizardState.currentStep - 1] || ''}`} />
      )}
    </div>
  );
}

// Kontext a nabízené dotazy pro Poradce podle aktuální stránky portálu
function assistantContextFor(view, step, typPodnikani) {
  const wizExamples = ['Kde najdu COGS ve výsledovce?', 'Co patří do OPEX?', 'Co znamená RRS?'];
  switch (view) {
    case 'wizard':
      return { context: `Průvodce oceněním/auditem, krok ${step}: ${popiskyProTyp(typPodnikani)[step - 1] || ''}`, suggestions: wizExamples };
    case 'billing':
      return { context: 'Stránka Tarify (FREE, PRO valuace, Audit podniku, Komplet).',
        suggestions: ['Jaký tarif se mi hodí?', 'Čím se liší PRO a Audit?', 'Můžu dokoupit audit později?'] };
    case 'dashboard':
    case 'detail':
    case 'report':
      return { context: 'Přehled / report uloženého ocenění a auditu klienta.',
        suggestions: ['Co znamenají tyhle metriky?', 'Jak dokoupím audit k této valuaci?', 'Jak hodnotu firmy zvýším?'] };
    case 'consult':
      return { context: 'Stránka Konzultace s týmem M&Ai.',
        suggestions: ['Kdy se ozve poradce?', 'S čím mi M&Ai pomůže?'] };
    default:
      return { context: 'Portál M&Ai pro ocenění a audit podniku.',
        suggestions: ['Co je valuace podniku?', 'Co umí audit?', 'Jak začít?'] };
  }
}

// Čistý „report" pohled na uloženou/zaplacenou analýzu (bez krokového panelu průvodce).
const TIER_BADGE = { free: 'FREE', rozsirena: 'ROZŠÍŘENÁ', pro: 'PRO valuace', audit: 'Audit', komplet: 'Kompletní' };
function ReportView({ wiz, bypassPayment, goBack }) {
  const ws = wiz.wizardState;
  const name = ws.company?.company_name || 'Ocenění firmy';
  const tier = ws.selectedPlan || 'free';
  return (
    <div className="wizard-container">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.6rem', marginBottom: '1rem' }}>
        <button onClick={goBack} style={{ background: 'transparent', border: 'none', color: '#6B3FA0', cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem', padding: 0 }}>
          ← Zpět na Ocenění a audit
        </button>
        <span style={{ padding: '0.25rem 0.75rem', borderRadius: 999, background: 'rgba(91,59,140,0.1)', color: '#6B3FA0', fontSize: '0.75rem', fontWeight: 800 }}>
          {TIER_BADGE[tier] || tier}
        </span>
      </div>
      <div style={{ marginBottom: '1.25rem' }}>
        <h1 style={{ margin: 0, fontFamily: "'Source Serif 4', Georgia, serif", color: '#1A1528', fontSize: '1.5rem' }}>{name}</h1>
        {ws.segment_id && <div style={{ color: '#6B6680', fontSize: '0.85rem', marginTop: '0.2rem' }}>{ws.segment_id}</div>}
      </div>
      <ErrorBoundary onReset={goBack}>
        <Step6_Results
          wizardState={ws}
          setWizardState={wiz.setWizardState}
          onReset={() => { wiz.reset(); goBack(); }}
          onBack={goBack}
          bypassPayment={bypassPayment}
        />
      </ErrorBoundary>
    </div>
  );
}

// Stav průvodce bez těžkých reportů (ty se ukládají zvlášť do `reports`)
function serializeWizard(ws) {
  return { ...ws, aiReport: null, auditResult: null };
}
function extractReports(ws) {
  return { aiReport: ws.aiReport || null, auditResult: ws.auditResult || null, selectedPlan: ws.selectedPlan || 'free' };
}
// Z DB řádku poskládá kompletní wizardState pro znovuotevření valuace
function rebuildWizardFromRow(row) {
  const env = row?.input_json || {};
  const base = env.wizard_state;
  if (!base || !base.valuationResult) return null; // starý záznam bez stavu → nelze obnovit
  const reports = env.reports || {};
  return {
    ...base,
    aiReport: reports.aiReport || null,
    auditResult: reports.auditResult || null,
    selectedPlan: reports.selectedPlan || base.selectedPlan || 'free',
    savedValuationId: row.id,
    // Výsledky jsou vždy poslední krok, počet kroků závisí na typu podnikání.
    currentStep: pocetKroku(base.typ_podnikani),
  };
}

/** Přihlášený portál, rámec se sidebar navigací + auto-uložení výsledku do DB. */
function PortalShell({ wiz }) {
  const { user, signOut } = useAuth();
  const role = user?.app_metadata?.role;
  const isStaff = ['team', 'admin'].includes(role);
  const isAdmin = role === 'admin';
  const [view, setView] = useState('home');
  const [detailRow, setDetailRow] = useState(null);
  const savedRef = useRef(null);
  const userName = user?.user_metadata?.full_name || '';

  // Sjednoceno: jedna položka „Ocenění a audit" (seznam + spuštění nové analýzy).
  // Z jednoho vyplnění vzniká ocenění firmy i audit, vybírá se na konci.
  const navItems = [
    { key: 'home', label: 'Domů', icon: 'home' },
    { key: 'dashboard', label: 'Ocenění a audit', icon: 'dashboard' },
    { key: 'billing', label: 'Tarify', icon: 'billing' },
    { key: 'consult', label: 'Konzultace', icon: 'consult' },
    { key: 'account', label: 'Můj účet', icon: 'account' },
    ...(isStaff ? [{ key: 'users', label: 'Uživatelé', icon: 'users' }] : []),
    ...(isAdmin ? [{ key: 'crawler', label: 'Crawler', icon: 'crawler' }] : []),
  ];
  // Při průvodci/reportu/detailu nech v menu zvýrazněnou položku „Ocenění a audit"
  const activeNav = (view === 'wizard' || view === 'detail' || view === 'report') ? 'dashboard' : view;

  // Auto-uložení po dokončení výpočtu (jednou na výsledek), vč. celého stavu průvodce
  useEffect(() => {
    const res = wiz.wizardState.valuationResult;
    if (!res || savedRef.current === res) return;
    // Neukládat znovu po obnovení z DB (už má id)
    if (wiz.wizardState.savedValuationId) { savedRef.current = res; return; }
    savedRef.current = res;
    try {
      const input = buildValuationRequest(wiz.wizardState);
      saveValuation({
        input,
        result: res,
        companyName: wiz.wizardState.company?.company_name || '',
        segment: wiz.wizardState.segment_id,
        tier: wiz.wizardState.selectedPlan || 'free',
        wizardState: serializeWizard(wiz.wizardState),
        reports: extractReports(wiz.wizardState),
      })
        .then(row => { if (row?.id) wiz.setWizardState(prev => ({ ...prev, savedValuationId: row.id })); })
        .catch(() => {/* tichý fail, uložení je nice-to-have */});
    } catch { /* ignore */ }
  }, [wiz.wizardState.valuationResult]); // eslint-disable-line react-hooks/exhaustive-deps

  // Obnoví celý stav průvodce do výsledků (z DB řádku nebo snapshotu) a volitelně po platbě vygeneruje report
  const restoreToResults = (state, { pendingGenerate } = {}) => {
    if (!state) return false;
    savedRef.current = state.valuationResult || savedRef.current; // ať se znovu neukládá
    wiz.setWizardState({ ...state, currentStep: pocetKroku(state.typ_podnikani), ...(pendingGenerate ? { pendingGenerate } : {}) });
    setView('report'); // čistý report pohled (ne wizard chrome)
    return true;
  };

  // Návrat ze Stripe (?purchase=success): BULLETPROOF, primárně z DB (přežije i ztrátu snapshotu/jiné zařízení)
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    // Opuštěná platba: snapshot s finančními daty nemá zůstat na (sdíleném)
    // počítači. Starší než hodinu smažeme hned při startu.
    try {
      const snapRaw = JSON.parse(localStorage.getItem('mnai_resume_purchase') || 'null');
      if (snapRaw && (!snapRaw.ts || Date.now() - snapRaw.ts > 3600000)) {
        localStorage.removeItem('mnai_resume_purchase');
      }
    } catch { localStorage.removeItem('mnai_resume_purchase'); }
    if (p.get('purchase') !== 'success') return;
    const tier = p.get('tier');
    const valuationId = p.get('valuation_id');
    // Vyčisti URL (ať reload neopakuje obnovu)
    window.history.replaceState({}, '', window.location.pathname);

    let snap = null;
    try { snap = JSON.parse(localStorage.getItem('mnai_resume_purchase') || 'null'); } catch { /* ignore */ }
    localStorage.removeItem('mnai_resume_purchase');

    // 1) Rychlá cesta: snapshot ze stejného prohlížeče
    if (snap && snap.state && tier) {
      restoreToResults(snap.state, { pendingGenerate: tier });
      return;
    }
    // 2) Durable cesta: načti valuaci z DB podle id z URL a vygeneruj zaplacený report
    if (valuationId && tier) {
      getValuation(valuationId)
        .then(row => {
          const state = rebuildWizardFromRow(row);
          if (state) restoreToResults(state, { pendingGenerate: tier });
          else setView('dashboard');
        })
        .catch(() => setView('dashboard'));
      return;
    }
    // 3) Nouze: ukaž přehled valuací (entitlement je uložený, klient může otevřít a dogenerovat)
    setView('dashboard');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Otevření uložené valuace → obnoví celý průvodce do výsledků (lze dokoupit rozšíření).
  // Řádky z výpisu jsou odlehčené (bez input_json), proto dotáhneme plný řádek z DB.
  const [opening, setOpening] = useState(false);
  const openSaved = async (v) => {
    if (!v?.id) { setDetailRow(v); setView('detail'); return; }
    setOpening(true);
    try {
      const row = await getValuation(v.id);
      const state = rebuildWizardFromRow(row);
      if (state) restoreToResults(state);
      else { setDetailRow(row); setView('detail'); } // starý záznam bez stavu → read-only náhled
    } catch {
      setDetailRow(v); setView('detail');
    } finally {
      setOpening(false);
    }
  };

  const newValuation = () => {
    wiz.reset();
    // Předvyplnit kontakt z účtu (uživatel je přihlášený)
    wiz.setWizardState(prev => ({
      ...prev,
      company: {
        ...prev.company,
        company_name: user?.user_metadata?.company_name || prev.company.company_name || '',
        contact_name: user?.user_metadata?.full_name || '',
        contact_email: user?.email || '',
      },
    }));
    savedRef.current = null;
    setView('wizard');
  };
  const select = (key) => { if (key === 'wizard') newValuation(); else setView(key); };

  // ── Historie prohlížeče: tlačítko Zpět naviguje UVNITŘ portálu, nevyhodí na web ──
  // Lokaci kódujeme jako token; ve wizardu i podle kroku, ať Zpět prochází krok po kroku.
  const locKey = view === 'wizard' ? `wizard:${wiz.wizardState.currentStep}` : view;
  const isPopping = useRef(false);
  const lastKey = useRef(null);

  useEffect(() => {
    if (lastKey.current === null) {
      lastKey.current = locKey;
      window.history.replaceState({ locKey }, '');
      return;
    }
    if (isPopping.current) { isPopping.current = false; lastKey.current = locKey; return; }
    if (locKey !== lastKey.current) {
      window.history.pushState({ locKey }, '');
      lastKey.current = locKey;
    }
  }, [locKey]);

  useEffect(() => {
    const onPop = (e) => {
      const key = (e.state && e.state.locKey) || 'home';
      isPopping.current = true;
      if (key.startsWith('wizard:')) {
        const step = parseInt(key.split(':')[1], 10) || 1;
        wiz.setWizardState(prev => ({ ...prev, currentStep: step }));
        setView('wizard');
      } else {
        setView(key);
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Zpět v rámci portálu = krok zpět v historii (sjednoceno s tlačítkem prohlížeče)
  const goBack = () => window.history.back();

  if (opening) {
    return <PortalLayout items={navItems} active={activeNav} onSelect={select} email={user?.email} onLogout={signOut}>
      <div style={{ padding: '4rem 1rem', textAlign: 'center', color: '#6B6680' }}>Načítám valuaci…</div>
    </PortalLayout>;
  }

  const assist = assistantContextFor(view, wiz.wizardState.currentStep, wiz.wizardState.typ_podnikani);

  return (
    <PortalLayout items={navItems} active={activeNav} onSelect={select} email={user?.email} onLogout={signOut}>
      {view === 'home' && <Home isStaff={isStaff} isAdmin={isAdmin} userName={userName} onNavigate={setView} onNew={newValuation} onOpen={openSaved} />}
      {view === 'dashboard' && <Dashboard isStaff={isStaff} onOpen={openSaved} onNew={newValuation} onNavigate={setView} />}
      {view === 'detail' && detailRow && <ValuationDetail valuation={detailRow} onBack={goBack} onNew={newValuation} />}
      {view === 'wizard' && <WizardView {...wiz} bypassPayment={isStaff} goBack={goBack} hideAssistant />}
      {view === 'report' && <ReportView wiz={wiz} bypassPayment={isStaff} goBack={goBack} />}
      {view === 'billing' && <Billing onStartValuation={newValuation} isStaff={isStaff} />}
      {view === 'consult' && <Consultation isStaff={isStaff} />}
      {view === 'account' && <Account />}
      {view === 'users' && isStaff && <AdminPanel canEdit={isAdmin} />}
      {view === 'crawler' && isAdmin && <Crawler />}
      {/* AI Poradce dostupný na všech stránkách portálu */}
      <AssistantWidget context={assist.context} suggestions={assist.suggestions} />
    </PortalLayout>
  );
}

/** Login-first: rozhoduje mezi přihlášením a portálem. */
function PortalApp({ wiz }) {
  const { loading, user } = useAuth();
  // Deep-link z webu: ?login nebo ?register otevře rovnou přihlašovací obrazovku
  const [authMode] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.has('register')) return 'register';
    if (p.has('login')) return 'login';
    return null;
  });

  if (loading) return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0D0B1A', color: '#C89EF5' }}>Načítání…</div>;
  if (user) return <PortalShell wiz={wiz} />;
  // Login-first: bez přihlášení se do kalkulačky nedostaneš
  return <AuthScreen initialMode={authMode || 'login'}
    intro="Přihlaste se a spočítejte hodnotu svého podniku, vše zůstane uložené ve vašem portálu." />;
}

function App() {
  const wiz = useWizardState();

  if (isSupabaseConfigured) {
    return (
      <AuthProvider>
        <PortalApp wiz={wiz} />
      </AuthProvider>
    );
  }

  return (
    <PasswordGate>
      <WizardView {...wiz} />
    </PasswordGate>
  );
}

export default App;
